/* eslint-disable no-unused-vars */
import { useMemo, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import GetAppIcon from "@material-ui/icons/GetApp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { IconButton, Tooltip } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../components/Table/Table";
import { Header } from "../../../components";
import { ApproverApprovedAndRejectedBaseColumns } from "../../../utils/utils";
import TicketButton from "../../../components/TableComponents/TicketButton/TicketButton";
import TableLayout from "../../../layouts/TableLayout/TableLayout";
import useTicketData from "../../../hooks/useTicketData";
import useTicketFilters from "../../../utils/ticketFilters";
import ActionButtons from "../../../components/TableComponents/ActionButtons/ActionButtons";
import { ROUTES } from "../../../constants";
import useTicketExport from "../../../hooks/useTicketExport";
import PhysicalDocumentToggle from "../../../components/TableComponents/PhysicalDocumentToggle/PhysicalDocumentToggle";
import NoItemsFound from "../../../components/NoItemsFound/NoItemsFound";
import ConfirmCloseModal from "../../../components/ConfirmCloseModal/ConfirmCloseModal";
import { API } from "../../../apis/api";
import {
  errorSnackbar,
  successSnackbar,
} from "../../../redux/features/customSnackbar/action";
import useDocumentViewer from "../../../hooks/useDocumentViewer";
import DocumentViewerModal from "../../../components/DocumentViewerModal/DocumentViewerModal";

function Close() {
  const history = useHistory();
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState("");
  const userId = useSelector((state)=>state.auth.user.userId)
  // Custom hooks
  const {
    searchQuery,
    allRows: filteredRows,
    loading,
    isRefreshing,
    handleRefresh,
    handleSearchQuery,
    total,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTicketData("E_VERIFICATION", true);

  const {
    documentViewerModal,
    handleDownloadFile,
    handleViewDocument,
    handleCloseDocumentViewer,
  } = useDocumentViewer();

  const handleOpenModal = () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleActionClick = useCallback((action, rowData) => {
    if (action === "Close") {
      // Handle the action for resubmitting the ticket
      setSelectedRow(rowData);
      handleOpenModal();
    }
  }, []);

  const handleCloseTicket = async (ticketNumber, ticketId) => {
    try {
      const payload = {
        ticket_code: ticketNumber,
        sub:userId,
        ticket_status: "CLOSED",
      };

      const response = await API.post(`ticket/update-ticket-status/`, payload);
      if (response) {
        await handleRefresh();
        dispatch(
          successSnackbar({
            title: "Ticket Closed Successfully",
            subtitle: `Ticket No. ${ticketNumber} has been successfully closed.`,
          })
        );
      }
    } catch (err) {
      console.error(`Error Closing ticket ${ticketNumber}:`, err);
      dispatch(
        errorSnackbar({
          title: "Error",
          subtitle:
            err.data?.message ||
            "Failed to close the ticket. Please try again.",
        })
      );
    }
  };

  // Column configuration
  const columns = useMemo(() => {
    const updatedColumns = [...ApproverApprovedAndRejectedBaseColumns];

    updatedColumns[0] = {
      width: 180,
      title: "Ticket Number",
      field: "ticketNumber",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <TicketButton
          ticketNumber={rowData.ticketNumber}
          ticketId={rowData.id}
        />
      ),
    };

    updatedColumns[3] = {
      width: 260,
      title: "Physical Document Received",
      field: "physicalDocumentReceived",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => {
        const value = rowData.physicalDocumentReceived;

        return <PhysicalDocumentToggle checked={value} />;
      },
    };

    updatedColumns[4] = {
      width: 220,
      title: "Document",
      field: "document",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.1rem" }}>
          <span style={{ marginRight: "0.1rem" }}>{rowData.document}</span>

          <Tooltip title="Download">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                handleDownloadFile(rowData.ticketNumber, "physically_verified");
              }}
            >
              <GetAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="View">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                handleViewDocument(rowData.ticketNumber, "", "physically_verified");
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    };

    updatedColumns[5] = {
      width: 150,
      title: "Action",
      field: "action",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <ActionButtons
          actions={["Close"]}
          status={rowData.status}
          onActionClick={(action) => handleActionClick(action, rowData)}
        />
      ),
    };

    return updatedColumns;
  }, [handleActionClick]);

  const { handleExport, isExporting } = useTicketExport(
    "APPROVER_REJECTED",
    columns
  );

  return (
    <TableLayout
      styles={{ tableInnerWrapper: { paddingBottom: "0rem" } }}

      header={
        <Header
          searchQuery={searchQuery}
          handleSearchQuery={handleSearchQuery}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          onExport={handleExport}
          isExporting={isExporting}
          disableExport={filteredRows.length === 0}
        />
      }
    >
      {filteredRows.length === 0 && !(loading) ? (
        <NoItemsFound />
      ) : (
        <Table
          columns={columns}
          defaultColumns={columns}
          rows={filteredRows}
          hideCheckbox
          rowHeight={45}
          setHasActiveEdits={() => {}}
          isDataLoading={loading || isRefreshing}
          serverSide
          totalRows={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      <ConfirmCloseModal
        open={openModal}
        onClose={handleCloseModal}
        handleVerifyTicket={handleCloseTicket}
        selectedRow={selectedRow}
      />
      <DocumentViewerModal
        open={documentViewerModal.open}
        onClose={handleCloseDocumentViewer}
        documentUrl={documentViewerModal.documentUrl}
        documentName={documentViewerModal.documentName}
        documentType={documentViewerModal.documentType}
        isLoading={documentViewerModal.isLoading}
        error={documentViewerModal.error}
      />
    </TableLayout>
  );
}

export default Close;
